import React from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    useTheme,
    alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import SwanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";
import SwanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";

import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChatIcon from "@mui/icons-material/Chat";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";

const services = [
    {
        title: "AI-Powered RAG Solutions",
        description:
            "Custom Retrieval-Augmented Generation systems for K12. Instantly surface answers, policies, and regulations from your own content, SOPs, and guidance documents�at scale.",
        icon: SmartToyIcon,
        color: "#21dbb8",
    },
    {
        title: "Custom LLM Chat Bots",
        description:
            "Private, school- and department-specific chatbots powered by advanced language models. Secure, district-specific knowledge�ready to answer questions from staff, students, or parents.",
        icon: ChatIcon,
        color: "#4facfe",
    },
    {
        title: "AI-Driven Bid & Procurement Analytics",
        description:
            "AI tools for forecasting, bid analysis, award recommendations, and compliance for child nutrition RFPs and finance teams. Unlock instant, explainable insights from your data.",
        icon: AnalyticsIcon,
        color: "#fa709a",
    },
    {
        title: "Nutrient & Product Analysis",
        description:
            "Automate USDA-compliant nutrient and ingredient analysis for every menu, product, or bid. Our AI parses product specs, labels, and PDFs to save hundreds of hours each year.",
        icon: RestaurantIcon,
        color: "#38ef7d",
    },
    {
        title: "Custom Data Integrations",
        description:
            "Bridge your SIS, POS, accounting, and nutrition systems with AI-powered automation and robust APIs. Automate repetitive work, flag errors, and streamline audit prep.",
        icon: IntegrationInstructionsIcon,
        color: "#667eea",
    },
    {
        title: "Deep K12 Analytics & Visualization",
        description:
            "Unlock insights from daily operations. AI-driven dashboards for finance, meal counts, staff training, purchasing, forecasting, inventory, and more.",
        icon: DashboardIcon,
        color: "#f093fb",
    },
    {
        title: "Document AI & Invoice Automation",
        description:
            "Automated document processing for invoices, bid responses, compliance forms, and training logs. Upload and let AI do the rest, with review and approval workflows.",
        icon: DescriptionIcon,
        color: "#4fd1c5",
    },
];

export default function Labs() {
    const theme = useTheme();

    const glassCard = {
        background: alpha(theme.palette.common.white, 0.85),
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
        borderRadius: "20px",
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>

            {/* HERO */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{
                    width: "100vw",
                    marginLeft: "calc(-50vw + 50%)",
                    py: { xs: 8, md: 12 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `linear-gradient(180deg, 
                        #4dd4c7 0%, 
                        #2a9d8f 25%, 
                        #1a7a8a 50%,
                        ${theme.palette.primary.main} 75%,
                        ${theme.palette.primary.dark} 100%)`,
                }}
            >
                <Container maxWidth="md">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.4 }}
                    >
                        <Box
                            component="img"
                            src={SwanAiLabsLogo}
                            alt="Swan AI Labs"
                            sx={{
                                height: { xs: 80, md: 120 },
                                mb: 3,
                            }}
                        />
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2.2rem", md: "3rem" },
                                letterSpacing: "-0.03em",
                                mb: 2,
                            }}
                        >
                            Swan AI Labs
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "rgba(255,255,255,0.9)",
                                fontWeight: 400,
                                maxWidth: 650,
                                mx: "auto",
                                lineHeight: 1.6,
                            }}
                        >
                            Advanced K12 AI and analytics, purpose-built for child nutrition,
                            school finance, and their partners.
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            {/* SERVICES */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            mb: 2,
                            textAlign: "center",
                            color: theme.palette.primary.dark,
                        }}
                    >
                        Our AI Services
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            color: theme.palette.text.secondary,
                            maxWidth: 550,
                            mx: "auto",
                            mb: 6,
                        }}
                    >
                        Real AI solutions for real K12 challenges�not hype.
                    </Typography>
                </motion.div>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            lg: "1fr 1fr 1fr",
                        },
                        gap: 3,
                    }}
                >
                    {services.map((service, idx) => {
                        const Icon = service.icon;
                        return (
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ delay: idx * 0.06, duration: 0.4 }}
                                whileHover={{
                                    y: -6,
                                    transition: { type: "spring", stiffness: 300, damping: 20 }
                                }}
                                whileTap={{ scale: 0.98 }}
                                style={{ height: "100%" }}
                            >
                                <Card
                                    sx={{
                                        ...glassCard,
                                        height: "100%",
                                        p: 3,
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "box-shadow 0.3s ease",
                                        "&:hover": {
                                            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: "14px",
                                            background: alpha(service.color, 0.15),
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 28, color: service.color }} />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 1.5,
                                            color: theme.palette.primary.dark,
                                        }}
                                    >
                                        {service.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.7,
                                            flexGrow: 1,
                                        }}
                                    >
                                        {service.description}
                                    </Typography>
                                </Card>
                            </motion.div>
                        );
                    })}
                </Box>
            </Container>

            {/* CTA */}
            <Container maxWidth="md" sx={{ pb: { xs: 8, md: 12 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        sx={{
                            ...glassCard,
                            p: { xs: 4, md: 6 },
                            textAlign: "center",
                            background: `linear-gradient(135deg, 
                                ${alpha("#e0f7f5", 0.9)} 0%, 
                                ${alpha("#f0fffe", 0.9)} 100%)`,
                        }}
                    >
                        <Box
                            component="img"
                            src={SwanSolutionsLogo}
                            alt="Swan Solutions"
                            sx={{
                                height: { xs: 50, md: 70 },
                                mb: 3,
                            }}
                        />
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                mb: 2,
                                color: theme.palette.primary.dark,
                            }}
                        >
                            Transform Your District with AI
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.text.secondary,
                                maxWidth: 600,
                                mx: "auto",
                                mb: 4,
                                lineHeight: 1.7,
                            }}
                        >
                            We partner with school nutrition teams, K12 finance offices, and vendors
                            across the US to bring real AI value. Whether you need a district-wide
                            chatbot, instant product analysis, or smarter compliance workflows�we can
                            help you make it real.
                        </Typography>
                        <motion.div
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            transition={{ type: "spring", stiffness: 400, damping: 20 }}
                        >
                            <Button
                                href="/contact"
                                variant="contained"
                                size="large"
                                sx={{
                                    py: 1.5,
                                    px: 5,
                                    fontSize: "1.1rem",
                                    fontWeight: 700,
                                    borderRadius: "12px",
                                    background: `linear-gradient(135deg, 
                                        ${theme.palette.primary.main} 0%, 
                                        #1a9a8a 100%)`,
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                                    "&:hover": {
                                        boxShadow: `0 6px 28px ${alpha(theme.palette.primary.main, 0.45)}`,
                                    },
                                }}
                            >
                                Start Your AI Project
                            </Button>
                        </motion.div>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}